import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, PlayCircle, Download, RefreshCcw, Undo2 } from "lucide-react";

// P6 completion — mirrors data_integrity.py's own 7 detector categories
// exactly (student_visa, commercial_plan, owner_credit, actor_attribution,
// tasks, reports_ai, duplicates). No backend category-listing endpoint
// exists yet, so this is a deliberate, documented mirror of the backend's
// own fixed category set rather than a free-text filter — update both
// together if a category is ever added/renamed in data_integrity.py.
const DI_CATEGORIES = ["student_visa", "commercial_plan", "owner_credit", "actor_attribution",
  "tasks", "reports_ai", "duplicates"];

const ELIGIBILITY_TONE = {
  deterministic: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  manual_review: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
};
const STATUS_TONE = {
  applied: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  already_applied: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  failed: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  skipped_stale: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  manual_review: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  pending: "bg-muted text-muted-foreground border",
  rolled_back: "bg-muted text-muted-foreground border",
};

export const ApplyDialog = ({ batch, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState(null);       // R15: what an Apply could touch, per issue code
  const [picked, setPicked] = useState({});
  const expected = `REPAIR ${batch.batch_id}`;

  // R15: an Apply must NAME its scope - the server rejects an Apply that names none, so the dialog makes the operator choose issue codes.
  useEffect(() => {
    if (!open) return;
    setScope(null); setPicked({});
    api.get(`/admin/data-integrity/batches/${batch.batch_id}/scope`)
      .then((r) => setScope(r.data.issue_codes || []))
      .catch((e) => { setScope([]); toast.error(errDetail(e, "Could not load the repair scope")); });
  }, [open, batch.batch_id]);

  const codes = Object.keys(picked).filter((k) => picked[k]);
  const willApply = (scope || []).filter((r) => picked[r.issue_code]).reduce((n, r) => n + r.deterministic_pending, 0);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/data-integrity/batches/${batch.batch_id}/apply`, {
        confirmation_phrase: phrase, issue_codes: codes,
      });
      toast.success(`Applied ${data.applied}, stale ${data.skipped_stale}, failed ${data.failed}, already-applied ${data.already_applied}`);
      setOpen(false);
      setPhrase("");
      onApplied();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const selectable = (scope || []).filter((r) => r.deterministic_pending > 0);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid={`di-apply-open-${batch.batch_id}`} size="sm" variant="outline"
        disabled={!batch.eligible_deterministic_count} onClick={() => setOpen(true)}>
        Apply {batch.eligible_deterministic_count} deterministic repair{batch.eligible_deterministic_count === 1 ? "" : "s"}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply deterministic repairs — {batch.batch_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Choose exactly which kinds of repair to apply. Only pending findings this batch marked
            eligibility=&quot;deterministic&quot; in the chosen categories are changed; the <strong>{batch.manual_review_count}</strong> manual-review
            findings are never touched by this action. Each record's fingerprint is re-verified immediately
            before mutation; anything changed since preview is skipped as stale, not overwritten.
          </p>
          <div data-testid="di-scope" className="space-y-1 max-h-48 overflow-auto">
            {scope === null && <p className="text-xs text-muted-foreground">Loading scope…</p>}
            {scope !== null && selectable.length === 0 && <p data-testid="di-scope-empty" className="text-xs text-muted-foreground">No pending deterministic repairs in this batch.</p>}
            {selectable.map((r) => (
              <label key={r.issue_code} className="flex items-center gap-2 text-xs">
                <input data-testid={`di-scope-${r.issue_code}`} type="checkbox" checked={!!picked[r.issue_code]}
                  onChange={(e) => setPicked((p) => ({ ...p, [r.issue_code]: e.target.checked }))} />
                <span className="font-mono">{r.issue_code}</span>
                <span className="text-muted-foreground">{r.deterministic_pending} pending</span>
              </label>
            ))}
          </div>
          <p data-testid="di-scope-total" className="text-xs">This Apply would change up to <strong>{willApply}</strong> record(s) in {codes.length} categor{codes.length === 1 ? "y" : "ies"}.</p>
          <div className="space-y-1.5">
            <Label>Type the exact confirmation phrase to proceed</Label>
            <p className="text-xs font-mono bg-muted px-2 py-1 rounded">{expected}</p>
            <Input data-testid="di-confirmation-phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)}
              placeholder={expected} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="di-apply-confirm" disabled={busy || phrase !== expected || codes.length === 0} onClick={apply}>
            {busy ? "Applying…" : "Apply now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// P6-completion correction (blocker 1) — Leadership confirms the actual
// performer for a PERFORMER_ATTRIBUTION_ASSIGNMENT_ONLY finding, right
// from this same findings table row. Single-record, reasoned, audited —
// never a bulk action.
const ConfirmPerformerDialog = ({ finding, onConfirmed }) => {
  const [open, setOpen] = useState(false);
  const [performerUserId, setPerformerUserId] = useState(finding.related_ids?.assigned_counsellor_user_id || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/data-integrity/appointments/${finding.record_id}/confirm-performer`, {
        performer_user_id: performerUserId, reason,
      });
      toast.success("Performer confirmed and audited");
      setOpen(false);
      setReason("");
      onConfirmed();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="ghost" data-testid={`di-confirm-performer-open-${finding.finding_id}`}
        onClick={() => setOpen(true)} title="Confirm who actually conducted this consultation">
        Confirm performer
      </Button>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirm actual performer</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            This consultation is assigned to <strong>{finding.current_state?.counsellor_name || finding.related_ids?.assigned_counsellor_user_id}</strong>,
            but nothing independently confirms they actually conducted it. Confirming here is a single-record,
            audited correction — never a bulk conversion of assignment into fact.
          </p>
          <div className="space-y-1.5">
            <Label>Actual performer&apos;s user ID</Label>
            <Input data-testid="di-confirm-performer-user-id" value={performerUserId}
              onChange={(e) => setPerformerUserId(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Reason (required)</Label>
            <Input data-testid="di-confirm-performer-reason" value={reason}
              onChange={(e) => setReason(e.target.value)} placeholder="How was this confirmed?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="di-confirm-performer-submit" disabled={busy || !performerUserId || !reason.trim()}
            onClick={submit}>
            {busy ? "Confirming…" : "Confirm performer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FindingsTable = ({ batchId, refreshTick, onChanged }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [eligibility, setEligibility] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [includeTest, setIncludeTest] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  const pageSize = 20;

  useEffect(() => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (eligibility !== "all") params.set("eligibility", eligibility);
    if (category !== "all") params.set("category", category);
    if (status !== "all") params.set("status", status);
    if (includeTest) params.set("include_test", "true");
    api.get(`/admin/data-integrity/batches/${batchId}/findings?${params}`).then((r) => setData(r.data));
  }, [batchId, page, eligibility, category, status, includeTest, refreshTick]);

  if (!data) return <p className="text-xs text-muted-foreground">Loading findings…</p>;

  const rollback = async (findingId) => {
    setRollingBack(findingId);
    try {
      const { data: result } = await api.post(`/admin/data-integrity/findings/${findingId}/rollback`);
      toast.success(result.status === "rolled_back" ? "Rolled back to the prior state"
        : "Could not roll back — newer activity has moved the record since (forward-fix only)");
      onChanged();
    } catch (e) { toast.error(errDetail(e)); } finally { setRollingBack(null); }
  };

  return (
    <div className="space-y-2" data-testid="di-findings-table">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={eligibility} onValueChange={(v) => { setEligibility(v); setPage(1); }}>
          <SelectTrigger data-testid="di-eligibility-filter" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All eligibility</SelectItem>
            <SelectItem value="deterministic">Deterministic only</SelectItem>
            <SelectItem value="manual_review">Manual review only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger data-testid="di-category-filter" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {DI_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="di-status-filter" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_TONE).map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant={includeTest ? "default" : "outline"} data-testid="di-include-test-toggle"
          onClick={() => { setIncludeTest((v) => !v); setPage(1); }}
          title="By default, findings on records tagged is_test_record=true are excluded from this view and from repairs — toggle to reveal them">
          {includeTest ? "Showing TEST records" : "Include TEST records"}
        </Button>
        <span className="text-xs text-muted-foreground" data-testid="di-findings-total">
          {data.total} findings{!includeTest ? " (business + legacy/unknown only — TEST excluded by default)" : ""}
        </span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Issue</TableHead><TableHead>Record</TableHead>
            <TableHead>Population</TableHead>
            <TableHead>Eligibility</TableHead><TableHead>Status</TableHead>
            <TableHead className="hidden lg:table-cell">Reason</TableHead>
            <TableHead className="hidden lg:table-cell">Before → Proposed</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((f) => (
            <TableRow key={f.finding_id} data-testid="di-finding-row">
              <TableCell className="text-xs font-mono">{f.issue_code}</TableCell>
              <TableCell className="text-xs font-mono">{String(f.record_id).slice(0, 24)}</TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]" data-testid="di-finding-population">{f.population || "legacy_unknown"}</Badge></TableCell>
              <TableCell><Badge variant="outline" className={`text-[10px] ${ELIGIBILITY_TONE[f.eligibility] || ""}`}>{f.eligibility}</Badge></TableCell>
              <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_TONE[f.status] || ""}`}>{f.status}</Badge></TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-80 truncate" title={f.reason}>{f.reason}</TableCell>
              <TableCell className="hidden lg:table-cell text-xs font-mono text-muted-foreground max-w-64 truncate">
                {JSON.stringify(f.current_state)} {f.proposed_state ? `→ ${JSON.stringify(f.proposed_state)}` : ""}
              </TableCell>
              <TableCell className="flex items-center gap-1">
                {f.status === "applied" && (
                  <Button size="sm" variant="ghost" disabled={rollingBack === f.finding_id}
                    data-testid={`di-rollback-${f.finding_id}`} onClick={() => rollback(f.finding_id)}
                    title="Roll back to the state before this repair was applied (refused if newer activity has since changed the record)">
                    <Undo2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                {f.issue_code === "PERFORMER_ATTRIBUTION_ASSIGNMENT_ONLY" && (
                  <ConfirmPerformerDialog finding={f} onConfirmed={onChanged} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
        <span className="text-xs text-muted-foreground">Page {data.page} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
        <Button size="sm" variant="outline" disabled={page * pageSize >= data.total} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </div>
  );
};

export const DataIntegrityTab = () => {
  const [batches, setBatches] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadBatches = () => {
    api.get("/admin/data-integrity/batches").then((r) => {
      setBatches(r.data);
      if (!selected && r.data.length) setSelected(r.data[0].batch_id);
    });
  };

  useEffect(() => { loadBatches(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/data-integrity/dry-run");
      const biz = data.population_counts?.business?.total ?? "?";
      const test = data.population_counts?.test?.total ?? "?";
      const legacy = data.population_counts?.legacy_unknown?.total ?? "?";
      toast.success(`Dry run complete — Business: ${biz}, TEST: ${test}, Legacy/unknown: ${legacy} `
        + `(${data.eligible_deterministic_count} deterministic, ${data.manual_review_count} manual-review overall)`);
      setSelected(data.batch_id);
      loadBatches();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const exportBatch = async (batchId) => {
    try {
      const { data } = await api.get(`/admin/data-integrity/batches/${batchId}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${batchId}-findings.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const batch = batches?.find((b) => b.batch_id === selected);

  return (
    <div className="space-y-4" data-testid="data-integrity-tab">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> Historical Data Integrity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Audits historical records against current canonical business rules. A dry run never mutates
            business data. Applying only ever touches findings this tool itself marked deterministic —
            manual-review findings are never auto-applied and require a separate, explicit human decision
            outside this tool.
          </p>
          <div className="flex gap-2">
            <Button data-testid="di-run-dry-run" size="sm" disabled={busy} onClick={runDryRun}>
              <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Running…" : "Run new dry-run audit"}
            </Button>
            <Button size="sm" variant="outline" onClick={loadBatches}>
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {batches && batches.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Batch history</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead><TableHead>Started</TableHead><TableHead>Mode</TableHead>
                  <TableHead>Business</TableHead><TableHead>TEST</TableHead><TableHead>Legacy/unknown</TableHead>
                  <TableHead>Status</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((b) => (
                  <TableRow key={b.batch_id} data-testid="di-batch-row"
                    className={b.batch_id === selected ? "bg-muted/50" : "cursor-pointer"}
                    onClick={() => setSelected(b.batch_id)}>
                    <TableCell className="text-xs font-mono">{b.batch_id}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtIST(b.started_at)}</TableCell>
                    <TableCell className="text-xs">{b.mode}</TableCell>
                    <TableCell className="text-xs" data-testid="di-batch-population-business">{b.population_counts?.business?.total ?? "—"}</TableCell>
                    <TableCell className="text-xs" data-testid="di-batch-population-test">{b.population_counts?.test?.total ?? "—"}</TableCell>
                    <TableCell className="text-xs" data-testid="di-batch-population-legacy">{b.population_counts?.legacy_unknown?.total ?? "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{b.status}</Badge></TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); exportBatch(b.batch_id); }}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {batch && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-heading">Findings — {batch.batch_id}</CardTitle>
            <ApplyDialog batch={batch} onApplied={() => { loadBatches(); setRefreshTick((t) => t + 1); }} />
          </CardHeader>
          <CardContent>
            <FindingsTable batchId={batch.batch_id} refreshTick={refreshTick}
              onChanged={() => setRefreshTick((t) => t + 1)} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
